import { useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState, EmptyState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { useT } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';

type Filters = { hasDiscrepancy: boolean; incomplete: boolean; unpriced: boolean; awaitingDisposition: boolean; importConflicts: boolean };

const CHIPS: { key: keyof Filters; labelKey: TranslationKey }[] = [
  { key: 'hasDiscrepancy', labelKey: 'problems.hasDiscrepancy' },
  { key: 'incomplete', labelKey: 'problems.incomplete' },
  { key: 'unpriced', labelKey: 'problems.unpriced' },
  { key: 'awaitingDisposition', labelKey: 'problems.awaitingDisposition' },
  { key: 'importConflicts', labelKey: 'overview.importConflicts' },
];

/**
 * Unified read-side over every problem category the backend exposes
 * (receiving discrepancies, unresolved return dispositions, import
 * conflicts, unpriced billing, incomplete shipments). Real enforcement of
 * "who sees what" happens server-side per category (GET /problems),
 * never as a UI-side filter.
 */
export function ProblemsPage(): JSX.Element {
  const [params] = useSearchParams();
  const [filters, setFilters] = useState<Filters>({
    hasDiscrepancy: params.get('hasDiscrepancy') === 'true',
    incomplete: params.get('incomplete') === 'true',
    unpriced: params.get('unpriced') === 'true',
    awaitingDisposition: params.get('awaitingDisposition') === 'true',
    importConflicts: params.get('importConflicts') === 'true',
  });
  const result = useApi(() => endpoints.problems(filters), [JSON.stringify(filters)]);
  const t = useT();

  function toggle(key: keyof Filters): void {
    setFilters((f) => ({ ...f, [key]: !f[key] }));
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t('problems.title')}</h1>
      </div>
      <div className="filters">
        {CHIPS.map((c) => (
          <label key={c.key}>
            <input type="checkbox" checked={filters[c.key]} onChange={() => toggle(c.key)} />
            {t(c.labelKey)}
          </label>
        ))}
      </div>

      {result.status === 'loading' && <LoadingState />}
      {result.status === 'error' && <ErrorBanner error={result.error} onRetry={result.reload} />}
      {result.status === 'success' &&
        (result.data.rows.length === 0 ? (
          <EmptyState title={t('problems.emptyTitle')} hint={t('problems.emptyHint')} />
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('overview.colType')}</th>
                <th>{t('overview.colEntity')}</th>
                <th>{t('overview.colSeller')}</th>
                <th>{t('overview.colSeverity')}</th>
                <th>{t('overview.colAge')}</th>
              </tr>
            </thead>
            <tbody>
              {result.data.rows.map((row) => (
                <tr key={`${row.type}-${row.entity_id}`} onClick={() => (window.location.href = hrefFor(row.type, row.entity_id))}>
                  <td>
                    <StatusBadge value={row.type} />
                  </td>
                  <td>{row.entity_label}</td>
                  <td>{row.seller_name ?? '—'}</td>
                  <td>{row.severity_label}</td>
                  <td>{t('overview.ageHours', { n: Math.floor(Number(row.age_seconds) / 3600) })}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        ))}
    </div>
  );
}

function hrefFor(type: string, entityId: string): string {
  switch (type) {
    case 'RETURN_AWAITING_DISPOSITION':
      return `/returns/${entityId}`;
    case 'IMPORT_CONFLICT':
      return `/imports/${entityId}`;
    case 'BILLING_UNPRICED':
      return `/billing`;
    case 'SHIPMENT_INCOMPLETE':
      return `/shipments/doc/${entityId}`;
    default:
      return '/inbounds';
  }
}
